import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { HC } from "../hr/theme";
import { Kpi, Bars, DataTable, cmoney, num } from "./crmui";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const PRESETS = [["today", "Today"], ["week", "This Week"], ["mtd", "Month to Date"], ["last_month", "Last Month"]];
const iso = (d) => d.toISOString().slice(0, 10);

function range(preset) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (preset === "today") return { from: iso(today), to: iso(today) };
  if (preset === "week") { const s = new Date(today); s.setDate(today.getDate() - ((today.getDay() + 6) % 7)); return { from: iso(s), to: iso(today) }; }
  if (preset === "last_month") {
    return { from: iso(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
             to: iso(new Date(today.getFullYear(), today.getMonth(), 0)) };
  }
  return { from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), to: iso(today) };
}

export default function CRMDashboard() {
  const navigate = useNavigate();
  const [preset, setPreset] = useState("mtd");
  const [filters, setFilters] = useState({ location_id: "", status: "", tier: "", channel: "", segment_id: "" });
  const [segments, setSegments] = useState([]);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const r = useMemo(() => range(preset), [preset]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { date_from: r.from, date_to: r.to };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/crm/dashboard", { params }).then(setD)
      .catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, [r.from, r.to, filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/crm/segments").then(setSegments).catch(() => {}); }, []);

  if (loading && !d) return <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[...Array(8)].map((_, i) => <div key={i} className="h-24 rounded-2xl border bg-white animate-pulse" style={{ borderColor: HC.border }} />)}</div>;
  if (!d) return null;
  const k = d.kpis;
  const sel = (label, key, options) => (
    <select value={filters[key]} onChange={(e) => setFilters({ ...filters, [key]: e.target.value })}
      data-testid={`crm-filter-${key}`} className="h-8 px-2 rounded-md border text-[12px]" style={{ borderColor: HC.border }}>
      <option value="">{label}</option>
      {options.map((o, i) => <option key={`${o.value}-${i}`} value={o.value}>{o.label}</option>)}
    </select>
  );

  return (
    <div className="space-y-5" data-testid="crm-dashboard">
      <div className="rounded-2xl bg-white border p-3 flex flex-wrap items-center gap-2" style={{ borderColor: HC.border }} data-testid="crm-dash-filters">
        {PRESETS.map(([key, label]) => (
          <button key={key} onClick={() => setPreset(key)} data-testid={`crm-preset-${key}`}
            className="h-8 px-3 rounded-md text-[12px] font-semibold border"
            style={preset === key ? { background: HC.burgundy, color: "#fff", borderColor: HC.burgundy } : { background: "#fff", color: HC.text, borderColor: HC.border }}>
            {label}
          </button>
        ))}
        <div className="flex-1 min-w-2" />
        {sel("All locations", "location_id", (d.locations || []).map((l) => ({ value: l.id, label: l.name })))}
        {sel("All statuses", "status", ["new", "active", "returning", "inactive"].map((s) => ({ value: s, label: s })))}
        {sel("All tiers", "tier", (d.tiers || []).map((t) => ({ value: t.name, label: t.name })))}
        {sel("All channels", "channel", ["pos", "mobile", "website", "admin"].map((c) => ({ value: c, label: c })))}
        {sel("All segments", "segment_id", segments.map((s) => ({ value: s.id, label: s.name })))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
        <Kpi testid="kpi-total-customers" label="Total Customers" value={num(k.total_customers)} onClick={() => navigate("/crm/customers")} />
        <Kpi testid="kpi-new-customers" label="New Customers" value={num(k.new_customers)} sub="in selected period" onClick={() => navigate("/crm/customers")} />
        <Kpi testid="kpi-active-customers" label="Active Customers" value={num(k.active_customers)} onClick={() => navigate("/crm/customers?status=active")} />
        <Kpi testid="kpi-returning-customers" label="Returning Customers" value={num(k.returning_customers)} />
        <Kpi testid="kpi-inactive-customers" label="Inactive Customers" value={num(k.inactive_customers)} />
        <Kpi testid="kpi-probation" label="On Probation" value={num(k.on_probation)} tone="amber" onClick={() => navigate("/crm/customers?verification=probation")} />
        <Kpi testid="kpi-verified" label="Verified Customers" value={num(k.verified_customers)} tone="green" />
        <Kpi testid="kpi-loyalty-members" label="Loyalty Members" value={num(k.loyalty_members)} onClick={() => navigate("/crm/loyalty")} />
        <Kpi testid="kpi-points-issued" label="Points Issued" value={num(k.points_issued)} />
        <Kpi testid="kpi-points-redeemed" label="Points Redeemed" value={num(k.points_redeemed)} />
        <Kpi testid="kpi-points-expired" label="Points Expired" value={num(k.points_expired)} />
        <Kpi testid="kpi-points-outstanding" label="Outstanding Points" value={num(k.points_outstanding)} tone="gold" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Bars testid="crm-new-vs-returning" title="New vs Returning" rows={d.new_vs_returning} />
        <Bars testid="crm-by-location" title="Customers by Location" rows={d.by_location} />
        <Bars testid="crm-by-channel" title="Customers by Registration Channel" rows={d.by_channel} />
        <Bars testid="crm-by-tier" title="Customers by Loyalty Tier" rows={d.by_tier} />

        <div className="rounded-2xl bg-white border p-5" style={{ borderColor: HC.border }} data-testid="crm-registration-trend">
          <h3 className="font-brand font-bold text-base mb-2" style={{ color: HC.text }}>Registration &amp; Activity Trend</h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={(d.registration_trend || []).map((x, i) => ({ name: x.name, registrations: x.value, purchases: (d.activity_trend || [])[i]?.value || 0 }))}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1E7D3" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: HC.soft }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: HC.soft }} axisLine={false} tickLine={false} width={34} />
              <Tooltip contentStyle={{ borderRadius: 12, border: `1px solid ${HC.border}` }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="registrations" stroke={HC.burgundy} fill={HC.burgundySoft} strokeWidth={2} />
              <Area type="monotone" dataKey="purchases" stroke={HC.green} fill={HC.greenTint} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl bg-white border p-5" style={{ borderColor: HC.border }} data-testid="crm-loyalty-trend">
          <h3 className="font-brand font-bold text-base mb-2" style={{ color: HC.text }}>Loyalty Activity Trend</h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={d.loyalty_trend || []}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1E7D3" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: HC.soft }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: HC.soft }} axisLine={false} tickLine={false} width={44} />
              <Tooltip contentStyle={{ borderRadius: 12, border: `1px solid ${HC.border}` }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="issued" stroke={HC.gold} fill={HC.goldSoft} strokeWidth={2} />
              <Area type="monotone" dataKey="redeemed" stroke={HC.purple} fill={HC.purpleTint} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DataTable testid="crm-top-spend" columns={[["name", "Customer"], ["value", "Lifetime spend"]]}
          rows={(d.top_by_spend || []).map((r) => ({ ...r, value: cmoney(r.value) }))}
          onRowClick={(r) => navigate(`/crm/customers/${r.id}`)} empty="No customer spend yet" />
        <DataTable testid="crm-top-frequency" columns={[["name", "Customer"], ["value", "Orders"]]}
          rows={d.top_by_frequency} onRowClick={(r) => navigate(`/crm/customers/${r.id}`)} empty="No orders yet" />
        <Bars testid="crm-pref-categories" title="Customer Preference — Categories" rows={d.preference_categories} />
        <Bars testid="crm-pref-products" title="Most Purchased Products" rows={d.preference_products} />
      </div>

      <div className="rounded-2xl bg-white border p-5" style={{ borderColor: HC.border }} data-testid="crm-marketing-analytics">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-brand font-bold text-base" style={{ color: HC.text }}>Marketing Analytics</h3>
          <span className="text-[11px] font-semibold px-2 py-1 rounded-full"
            style={d.marketing.integration_connected ? { background: HC.greenTint, color: HC.green } : { background: HC.amberTint, color: HC.amber }}>
            {d.marketing.integration_connected ? "Integration connected" : "No marketing platform connected"}
          </span>
        </div>
        {!d.marketing.integration_connected && (
          <div className="text-[12.5px] mb-3" style={{ color: HC.soft }}>
            Delivery, engagement and conversion metrics stay empty until CleverTap or Google Analytics is configured in CRM Settings — we never fabricate engagement data.
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi testid="mk-campaigns" label="Campaigns" value={num(d.marketing.campaigns_total)} />
          <Kpi testid="mk-sent" label="Campaigns Sent" value={num(d.marketing.campaigns_sent)} />
          <Kpi testid="mk-journey-enrolled" label="Journey Enrollments" value={num(d.marketing.journey_enrollments)} />
          <Kpi testid="mk-journey-completed" label="Journey Completions" value={num(d.marketing.journey_completions)} />
          <Kpi testid="mk-queued" label="Communications Queued" value={num(d.marketing.communications_queued)} />
          <Kpi testid="mk-blocked" label="Awaiting Integration" value={num(d.marketing.communications_blocked)} tone="amber" />
          <Kpi testid="mk-awaiting" label="Campaigns Awaiting Integration" value={num(d.marketing.campaigns_awaiting_integration)} tone="amber" />
          <Kpi testid="mk-tier-moves" label="Recent Tier Moves" value={num((d.tier_movement || []).length)} />
        </div>
      </div>
    </div>
  );
}
